import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Package, ShoppingCart, Warehouse, Tag, Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/tyretrack`;

const Statistics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const ownerEmail = user?.email || "";

  useEffect(() => {
    const fetchStats = async () => {
      if (!ownerEmail) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/statistics?owner_email=${encodeURIComponent(ownerEmail)}`);
        setStats(response.data);
      } catch (error) {
        toast.error("Errore nel caricamento delle statistiche");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [ownerEmail]);

  const getMaxValue = (obj) => {
    const values = Object.values(obj || {});
    return values.length > 0 ? Math.max(...values) : 1;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div data-testid="statistics-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate("/")}
          className="h-12 w-12 p-0"
        >
          <ArrowLeft size={24} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Statistiche</h1>
          <p className="text-muted-foreground text-sm">
            Panoramica del magazzino pneumatici
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Totale</p>
                <p className="text-3xl font-bold font-mono text-foreground">{stats.total_tires}</p>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Package className="text-primary" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">In Stock</p>
                <p className="text-3xl font-bold font-mono text-green-500">{stats.total_in_stock}</p>
              </div>
              <div className="h-12 w-12 bg-green-500/10 rounded-full flex items-center justify-center">
                <Warehouse className="text-green-500" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Venduti</p>
                <p className="text-3xl font-bold font-mono text-blue-500">{stats.total_sold}</p>
              </div>
              <div className="h-12 w-12 bg-blue-500/10 rounded-full flex items-center justify-center">
                <ShoppingCart className="text-blue-500" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* By Marca */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag size={20} className="text-primary" />
            Per Marca
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(stats.by_marca).length === 0 ? (
            <p className="text-muted-foreground text-center py-4">Nessun dato disponibile</p>
          ) : (
            Object.entries(stats.by_marca).slice(0, 10).map(([marca, count]) => (
              <div key={marca} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{marca}</span>
                  <span className="font-mono text-muted-foreground">{count} pz</span>
                </div>
                <Progress 
                  value={(count / getMaxValue(stats.by_marca)) * 100} 
                  className="h-2"
                />
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* By Magazzino */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Warehouse size={20} className="text-primary" />
            Per Magazzino
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(stats.by_magazzino).length === 0 ? (
            <p className="text-muted-foreground text-center py-4">Nessun dato disponibile</p>
          ) : (
            Object.entries(stats.by_magazzino).slice(0, 10).map(([mag, count]) => (
              <div key={mag} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{mag}</span>
                  <span className="font-mono text-muted-foreground">{count} pz</span>
                </div>
                <Progress 
                  value={(count / getMaxValue(stats.by_magazzino)) * 100} 
                  className="h-2"
                />
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* By Misura */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ruler size={20} className="text-primary" />
            Per Misura
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(stats.by_misura).length === 0 ? (
            <p className="text-muted-foreground text-center py-4">Nessun dato disponibile</p>
          ) : (
            Object.entries(stats.by_misura).slice(0, 10).map(([misura, count]) => (
              <div key={misura} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium font-mono">{misura}</span>
                  <span className="font-mono text-muted-foreground">{count} pz</span>
                </div>
                <Progress 
                  value={(count / getMaxValue(stats.by_misura)) * 100} 
                  className="h-2"
                />
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Statistics;
